import { Injectable } from '@angular/core';
import { CreatePayByLinkLiability } from '../models/create-pay-by-link-liability.model';
import { Observable, throwError } from 'rxjs';
import { PaymentApiService } from './payment-api.service';
import { catchError, map, tap } from 'rxjs/operators';
import { IntegrationVersionEnum, LiabilityDataModel } from '../models/liability-data.model';
import { PaymentMethod } from '../models/payment-method.model';
import { ImojePaymentService } from './imoje-payment.service';
import { ImojePayment } from '../models/imoje-payment.model';
import { Token } from '../models/token.model';
import { environment } from '../../environments/environment';
import { PaymentTypeEnum } from '../models/payment-type.model';
import { LinkParamsService } from './link-params.service';
import { ImojeAction } from '../models/imoje-action.model';
import { PlatformService } from './platform.service';
import { AvailableBillGatewayAppsModel } from '../models/available-bill-gateway-apps.model';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private TOKEN: string;
  private PAYMENT: LiabilityDataModel;

  constructor(
    private paymentApiService: PaymentApiService,
    private imojePaymentService: ImojePaymentService,
    private linkParamsService: LinkParamsService,
    private platformService: PlatformService
  ) {}

  get token(): string {
    return this.TOKEN;
  }

  set token(val: string) {
    this.TOKEN = val;
  }

  get payment(): LiabilityDataModel {
    return this.PAYMENT;
  }

  validateToken(body: CreatePayByLinkLiability): Observable<Token> {
    return this.paymentApiService.postPayByLinkLiabilities(body).pipe(catchError(err => throwError(err)));
  }

  getPayment(token: string = null) {
    return this.paymentApiService.getPayByLinkLiabilities(token || this.token).pipe(
      tap(data => {
        const integrationVersion = !!data.customer ? IntegrationVersionEnum.V0 : IntegrationVersionEnum.V1;
        this.PAYMENT = { integrationVersion, ...data };
        this.PAYMENT.email = this.linkParamsService.linkParams.email || this.PAYMENT.email;
        this.PAYMENT.name = this.linkParamsService.linkParams.name || this.PAYMENT.name;
        this.PAYMENT.surname = this.linkParamsService.linkParams.surname || this.PAYMENT.surname;
        this.PAYMENT.customer = this.linkParamsService.customer || this.PAYMENT.customer;
      })
    );
  }

  createImojePayment(
    paymentMethod: PaymentMethod,
    name: string,
    surname: string,
    email: string,
    paymentType: PaymentTypeEnum = PaymentTypeEnum.LIABILITY
  ): Observable<ImojePayment> {
    return this.imojePaymentService
      .createImojePayment(this.token, paymentMethod, name, surname, email, paymentType)
      .pipe(tap(i => this.imojePaymentService.submitImojePayment(i)));
  }

  createImojeTransaction(
    paymentMethod: PaymentMethod,
    name: string,
    surname: string,
    email: string,
    paymentType: PaymentTypeEnum = PaymentTypeEnum.LIABILITY,
    paymentMethodCode?: string
  ): Observable<ImojeAction> {
    return this.imojePaymentService
      .createImojeTransaction(this.token, paymentMethod, name, surname, email, paymentType, paymentMethodCode)
      .pipe(tap(i => this.imojePaymentService.submitImojeAction(i)));
  }

  createWireTransferTransaction(paymentType: PaymentTypeEnum, name: string, surname: string, email: string) {
    return this.imojePaymentService
      .createImojeTransaction(this.TOKEN, PaymentMethod.SIMP, name, surname, email, paymentType)
      .pipe(map(action => action.ban));
  }

  redirectToBillGatewayApp(options: RedirectToAppOptions = {}) {
    const { paymentType, token, personalData, connectApp } = {
      paymentType: PaymentTypeEnum.LIABILITY,
      personalData: {
        ...this.PAYMENT,
        ...this.linkParamsService.linkParams
      },
      ...options,
      token: options.token || this.token
    };

    const { name, surname, email } = personalData;

    const personalDataParams = `&name=${name}&surname=${surname}&email=${email}`;

    const appDetails: AvailableBillGatewayAppsModel = Object.values(environment.billGatewayApps).find(
      obj => obj.code === connectApp
    );
    const urlTemplate = appDetails.redirectLink[this.platformService.platform] || appDetails.redirectLink.desktop;
    let url = urlTemplate.replace('{{token}}', token).replace('{{personalDataParams}}', personalDataParams);

    if (paymentType === PaymentTypeEnum.BALANCE) {
      url += '&paymentType=balance';
    }

    window.location.href = url;
  }

  startPaymentAgain() {
    return this.imojePaymentService.startPaymentAgain(this.token);
  }
}

export interface RedirectToAppOptions {
  token?: string;
  connectApp?: string;
  personalData?: PersonalData;
  paymentType?: PaymentTypeEnum;
}

export interface PersonalData {
  name: string;
  surname: string;
  email: string;
}
